import React, { useMemo, useState } from 'react';
import { Card, Button, Spinner, Alert, Form } from 'react-bootstrap';
import { formatDateTime } from '../utils/format';
import { LoadingState } from './PageState';

/** How many comments show before "Show older". */
const VISIBLE_COMMENTS = 10;

const timeOf = (comment) => {
  const time = new Date(comment.createdAt).getTime();
  return Number.isNaN(time) ? null : time;
};

/**
 * Presentational company comments card, shared by the full company admin page
 * (CompanyDetail billing tab) and the focused CRM company page (CRMCompanyDetail).
 * Newest comments come first, under the composer, with older ones behind "Show older".
 *
 * Props:
 *  - comments, commentsLoading, commentError, onDismissError
 *  - newComment, onNewCommentChange, onAddComment, isPostingComment
 *  - canEdit
 *  - headerActions (optional): small buttons for the card header, e.g. <InvoiceActions compact />
 */
const CompanyComments = ({
  comments,
  commentsLoading,
  commentError,
  onDismissError,
  newComment,
  onNewCommentChange,
  onAddComment,
  isPostingComment,
  canEdit,
  headerActions = null,
}) => {
  const [showAll, setShowAll] = useState(false);

  // Newest first whatever order they arrive in; a new comment is appended to the end of the list
  const sorted = useMemo(() => comments
    .map((comment, index) => ({ comment, index, time: timeOf(comment) }))
    .sort((a, b) => {
      if (a.time !== null && b.time !== null && a.time !== b.time) return b.time - a.time;
      return b.index - a.index;
    })
    .map(({ comment }) => comment), [comments]);

  const visible = showAll ? sorted : sorted.slice(0, VISIBLE_COMMENTS);
  const hiddenCount = sorted.length - visible.length;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isPostingComment && newComment.trim()) onAddComment();
  };

  return (
    <Card className="shadow-sm mt-4">
      <Card.Header className="bg-light d-flex flex-wrap justify-content-between align-items-center gap-2">
        <h5 className="mb-0">Comments</h5>
        {headerActions && <div className="d-flex flex-wrap gap-2">{headerActions}</div>}
      </Card.Header>
      <Card.Body>
        {commentError && (
          <Alert variant="danger" dismissible onClose={onDismissError}>
            {commentError}
          </Alert>
        )}

        {canEdit && (
          <Form className="mb-4" onSubmit={handleSubmit}>
            <Form.Group className="mb-2" controlId="newComment">
              <Form.Label>Add a comment</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newComment}
                onChange={(e) => onNewCommentChange(e.target.value)}
                placeholder="Write a comment..."
              />
            </Form.Group>
            <Button
              type="submit"
              variant="outline-primary"
              disabled={isPostingComment || !newComment.trim()}
            >
              {isPostingComment && (
                <Spinner as="span" animation="border" size="sm" aria-hidden="true" className="me-2" />
              )}
              {isPostingComment ? 'Adding...' : 'Add comment'}
            </Button>
          </Form>
        )}

        {commentsLoading ? (
          <LoadingState label="Loading comments" className="py-3" />
        ) : sorted.length === 0 ? (
          <p className="text-muted mb-0">No comments yet.</p>
        ) : (
          <>
            <div className="d-flex flex-column">
              {visible.map((comment, i) => (
                <div
                  key={comment.id}
                  className={i === 0 ? 'pb-3' : 'py-3'}
                  style={i < visible.length - 1 ? { borderBottom: '1px solid var(--border-default)' } : undefined}
                >
                  <div className="d-flex flex-wrap justify-content-between align-items-baseline column-gap-3 mb-1">
                    <span className="fw-semibold text-break">{comment.createdByName || comment.createdByEmail || 'Unknown'}</span>
                    <span className="text-muted small text-nowrap">{formatDateTime(comment.createdAt, '')}</span>
                  </div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{comment.commentText}</div>
                </div>
              ))}
            </div>
            {hiddenCount > 0 && (
              <Button variant="link" size="sm" className="p-0 mt-2" onClick={() => setShowAll(true)}>
                Show {hiddenCount.toLocaleString('en-GB')} older {hiddenCount === 1 ? 'comment' : 'comments'}
              </Button>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default CompanyComments;
